import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import {
  CONTENT_TYPE_FIELD,
  CpsField,
  CpsFieldForView,
  CpsSearchQueryRequest,
  DEFAULT_FACETS_COUNT,
  DEFAULT_ROWS_PER_PAGE,
  DOC_ID_FIELD,
  EXTENSION_FIELD
} from '@cs-cms/types';
import { areQueryFiltersEqual } from './cpsGridHelpers';
import { buildFiltersFromComposite } from './cpsGridFilterBuilders';

export interface BuildInitialQueryRequestParams {
  columns: CpsFieldForView[];
  selectedView: number;
  selectedIndexId: number;
  currentViewGlobalFilters: { id: number; facetsCount?: number }[] | undefined;
  allFields: CpsField[] | undefined;
}

const buildViewFacets = (columns: CpsFieldForView[]): string[] =>
  columns
    .filter(field => field.facetable)
    .map(field => `${field.name},count:${field.facetsCount ?? DEFAULT_FACETS_COUNT}`);

const buildGlobalFilterFacets = (
  currentViewGlobalFilters: BuildInitialQueryRequestParams['currentViewGlobalFilters'],
  allFields: CpsField[] | undefined
): string[] =>
  (currentViewGlobalFilters
    ?.map(globalFilter => {
      const matchingField = allFields?.find(field => field.id === globalFilter.id);
      if (matchingField?.facetable) {
        return `${matchingField.name},count:${globalFilter.facetsCount ?? DEFAULT_FACETS_COUNT}`;
      }
      return null;
    })
    .filter((facet): facet is string => facet !== null) ?? []);

const mergeFacetStrings = (viewFacets: string[], globalFacets: string[]): string[] => {
  const facetMap = new Map<string, { facet: string; count: number }>();

  viewFacets.concat(globalFacets).forEach(facet => {
    const [fieldName, countPart] = facet.split(',count:');
    const count = parseInt(countPart, 10);
    const existing = facetMap.get(fieldName);
    if (!existing || existing.count < count) {
      facetMap.set(fieldName, { facet, count });
    }
  });

  return Array.from(facetMap.values()).map(item => item.facet);
};

const buildSelectColumns = (columns: CpsFieldForView[]): string[] =>
  columns
    .map(column => column.name)
    .concat([DOC_ID_FIELD, CONTENT_TYPE_FIELD, EXTENSION_FIELD])
    .filter((value, index, array) => array.indexOf(value) === index);

export const buildInitialCpsSearchQueryRequest = (
  params: BuildInitialQueryRequestParams
): CpsSearchQueryRequest => {
  const { columns, selectedView, selectedIndexId, currentViewGlobalFilters, allFields } = params;
  const viewFacets = buildViewFacets(columns);
  const globalFacets = buildGlobalFilterFacets(currentViewGlobalFilters, allFields);

  return {
    size: DEFAULT_ROWS_PER_PAGE,
    includeTotalCount: true,
    facets: mergeFacetStrings(viewFacets, globalFacets),
    select: buildSelectColumns(columns),
    indexId: selectedIndexId,
    viewId: selectedView,
    skip: 0
  };
};

export const applyGridFiltersToQueryRequest = (
  queryRequest: CpsSearchQueryRequest,
  compositeFilterDescriptor: CompositeFilterDescriptor | undefined,
  columns: CpsFieldForView[],
  translate: (key: string) => string
): CpsSearchQueryRequest | null => {
  const globalFilters = queryRequest.filters?.filter(filter => filter.isGlobal) ?? [];
  const { gridFilters, actionFilters } = buildFiltersFromComposite(compositeFilterDescriptor, columns, translate);
  const nextFilters = [...globalFilters, ...gridFilters];
  const nextActionsFilter = actionFilters.length > 0 ? actionFilters : undefined;

  const filtersChanged = !areQueryFiltersEqual(queryRequest.filters, nextFilters);
  const actionsChanged =
    (queryRequest.actionsFilter?.length ?? 0) !== (nextActionsFilter?.length ?? 0) ||
    queryRequest.actionsFilter?.some((v, i) => v !== nextActionsFilter?.[i]);

  if (!filtersChanged && !actionsChanged) {
    return null;
  }

  return {
    ...queryRequest,
    filters: nextFilters,
    actionsFilter: nextActionsFilter,
    skip: 0
  };
};
